import { ImsObject } from 'app/shared/collections/ims_object';
import ImsObjectCollection, { ImsObjectCollectionOptions } from './ims_object_collection.service';
import { IIntervalService, IRootScopeService, resource } from 'angular';
import { ViewStateService } from 'app/shared/view_state.service';
import { SiteStateService } from '../site_state.service';
import { StartupRegistry } from 'app/startup/registry/registry';
import { ILocalStorageService } from 'angular-local-storage';

export class ImsObjectIncrementalCollection<
    T extends ImsObject = any,
> extends ImsObjectCollection<T> {
    protected readonly _viewStateService: ViewStateService;
    protected readonly _siteStateService: SiteStateService;

    constructor(
        startUpRegistry: StartupRegistry,
        localStorageService: ILocalStorageService,
        $rootScope: IRootScopeService,
        $interval: IIntervalService,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        resourceObjectBase: resource.IResource<any>,
        imsObjectBase: any,
        options: ImsObjectCollectionOptions = {},
    ) {
        // bad things happen if the incremental collection is set to not be incremental
        // primarily relating to the fact that only incremental (read: probably empty)
        // updates will be given out and the collection will just delete itself instantly
        if (options.incremental !== true && options.incremental != null) {
            throw new Error(
                'IMSObjectIncrementalCollection must not be instantiated as non-incremental',
            );
        }
        super(
            startUpRegistry,
            localStorageService,
            $interval,
            resourceObjectBase,
            imsObjectBase,
            // Set up defaults!
            Object.assign({}, { onDemand: false, incremental: true, refreshRate: 10000 }, options),
        );
        this._viewStateService = viewStateService;
        this._siteStateService = siteStateService;

        this.resetCollection();

        siteStateService.onSiteSelectionChanged(this.refreshDataCallback.bind(this));
        viewStateService.onInitialViewLoad(this.refreshDataCallback.bind(this));
    }

    private refreshDataCallback() {
        if (this.isRunning()) {
            // Reset the initialization state of incremental collections when the site changes.
            // This will make all zyxAsync calls on the collection wait for the first
            // update of data from the API
            this.collectionNotInitialized = true;
            if (!this.onDemand) {
                this.resetCollection();
                this.refreshData();
            }
        }
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.resetCollection
     * @propertyOf ims.shared.data.service:ImsObjectIncrementalCollection
     * @description
     * Resets time to the begining, to ensure all objects are grabbed again,
     * and resets the collection, because it's grabbing all, so refresh them all.
     * Essentially imitates a non-incremental collection
     * @example Use when site changes for example, basically a hard refresh
     */
    resetCollection() {
        this.lastUpdatedFromApi = new Date(0);
        this.refreshCount = 0;
        super.resetCollection();
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.refreshData
     * @propertyOf ims.shared.data.service:ImsObjectIncrementalCollection
     * @description
     * Retrieves an updated list of resources from the API and updates this collection.
     */
    refreshData(params = {}, checkTime = false, useInFlightCheck = false) {
        // If there's no site yet, just return and wait for the site changed event.
        if (!this._siteStateService.selectedSiteId) {
            return Promise.resolve();
        }

        // Extend the params with the last_updated filter and the is_deleted filter for the first request
        // don't include the last updated filter if it's the first request
        const newParams = Object.assign(
            {
                last_updated__gt: this.lastUpdatedFromApi,
                // on the first refresh - don't pull in the deleted ones - because we don't care about them - so false;
                //  but after the first request we DO want to know when items get deleted; so undefined
                is_deleted: this.refreshCount === 0 ? false : undefined,
            },
            params,
        );
        return super.refreshData(newParams, checkTime, useInFlightCheck);
    }
}
